import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { articles } from "@/mocks/news";
import { formatHeat } from "@/utils/format";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const article = articles.find((a) => a.id === id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const current = total > 0 ? (window.scrollY / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, current)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const related = article
    ? articles.filter((a) => a.channel === article.channel && a.id !== article.id).slice(0, 3)
    : [];

  usePageMeta({
    title: article ? article.title : "内容不存在",
    description: article
      ? article.summary
      : "该瓜情内容可能已下线，请返回首页浏览最新动态。",
    keywords: article
      ? `${article.tags.join(",")},瓜田速递,突发专报,吃瓜前线`
      : "瓜田速递,突发专报",
    path: `/news/${id}`,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.summary,
            datePublished: article.publishedAt,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: "瓜田速递" },
            articleSection: article.channelName,
            keywords: article.tags.join(","),
            url: `https://cllcr.cn/news/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://cllcr.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: article.channelName,
                item: `https://cllcr.cn/dispatch/${article.channel}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://cllcr.cn/news/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">
              这条瓜情不存在或已下线
            </h1>
            <p className="mt-2 text-sm text-foreground-600">可能链接有误，也可能内容已被撤回。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回首页看最新
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }}></div>
      </div>

      <SiteHeader />
      <main className="flex-1">
        <article className="w-full">
          <header className="w-full bg-background-100/70 border-b border-background-200">
            <div className="w-full px-4 md:px-8 py-8 md:py-10">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: article.channelName, to: `/dispatch/${article.channel}` },
                  { label: "正文" },
                ]}
              />
              <div className="mt-5 max-w-4xl">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Link
                    to={`/dispatch/${article.channel}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-live-fill" aria-hidden="true"></i>
                    {article.channelName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md bg-background-50 border border-background-300 text-foreground-600 text-xs font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug text-balance">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                  {article.summary}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-label text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    约 {article.readTime} 分钟读完
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                    热度 {formatHeat(article.heat)}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-8">
                <figure className="rounded-lg overflow-hidden border border-background-200 mb-8">
                  <div className="w-full h-[240px] md:h-[380px]">
                    <img
                      src={article.image}
                      alt={`${article.title} 配图`}
                      title={`${article.title} - 瓜田速递`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <figcaption className="px-4 py-2.5 bg-background-100 text-xs text-foreground-500">
                    图片为示意配图，与事件当事人无关。
                  </figcaption>
                </figure>

                <div className="space-y-8">
                  {article.body.map((section, index) => (
                    <section key={section.heading}>
                      <h2 className="font-heading text-xl font-black text-foreground-950 flex items-center gap-2.5">
                        <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 font-heading text-xs font-black">
                          {index + 1}
                        </span>
                        {section.heading}
                      </h2>
                      <div className="mt-3.5 space-y-3.5">
                        {section.paragraphs.map((paragraph) => (
                          <p key={paragraph.slice(0, 24)} className="text-[15px] leading-7 text-foreground-700">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-10 rounded-lg border border-background-200 bg-secondary-50 p-5">
                  <p className="flex items-start gap-2.5 text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-information-line text-secondary-600 text-lg mt-0.5" aria-hidden="true"></i>
                    <span>
                      本站内容以公开信息整理与多方表述并列呈现为主，不构成对任何个人的定性评价。
                      若文中信息存在误差，欢迎通过邮箱联系我们更正。
                    </span>
                  </p>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                      <i className="ri-list-check-2 text-primary-500" aria-hidden="true"></i>
                      本篇脉络
                    </h2>
                    <ol className="mt-4 space-y-3">
                      {article.body.map((section, index) => (
                        <li key={section.heading} className="flex items-start gap-3 text-sm text-foreground-600">
                          <span className="font-heading text-xs font-black text-primary-400 mt-0.5">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {section.heading}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">同频道相关</h2>
                    {related.length > 0 ? (
                      <div className="mt-2">
                        {related.map((item, index) => (
                          <NewsCard key={item.id} article={item} variant="compact" index={index} />
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-foreground-500">暂无同频道相关内容。</p>
                    )}
                  </div>

                  <Link
                    to="/#subscribe"
                    className="flex items-center justify-between gap-3 rounded-lg bg-foreground-950 p-5 group cursor-pointer"
                  >
                    <span>
                      <span className="block font-heading text-sm font-bold text-background-50">
                        订阅瓜情简报
                      </span>
                      <span className="mt-1 block text-xs text-background-200/70">
                        重要进展不再错过
                      </span>
                    </span>
                    <i
                      className="ri-arrow-right-line text-accent-400 group-hover:translate-x-1 transition-transform"
                      aria-hidden="true"
                    ></i>
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section aria-label="相关推荐" className="w-full bg-background-100/70 border-t border-background-200">
            <div className="w-full px-4 md:px-8 py-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-black text-foreground-950">继续吃这一口</h2>
                <Link
                  to={`/dispatch/${article.channel}`}
                  className="text-xs font-label text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  查看{article.channelName}全部 <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {related.map((item) => (
                  <NewsCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}